import { AccountActions, IssueActions } from '../actions';
import Refluxr from '../../lib/refluxr';
import { _ } from '../utils';
import {
   markOptimisticMessage,
   reconcileOptimisticMessages,
   removeOptimisticMessage,
   upsertOptimisticMessage
} from './issueNoteState';

const EMPTY_SUMMARY = { total: 0, open: 0, closed: 0, unassigned: 0 };

const INITIAL_STATE = {
   issues: [],
   paging: { count: 0, total: 0, limit: 20, page: 1 },
   summary: EMPTY_SUMMARY,
   initialised: false,
   loadingInitial: false,
   refreshing: false,
   loadingMore: false,
   error: null,
   requestPage: 1,
   raiseIssueSaving: false,
   raiseIssueError: null,
   raiseIssueCompletedAt: null,
   raisedIssue: null,
   issuesRefreshPending: false,
   currentIssue: null,
   currentIssueProvisional: false,
   currentIssueLoading: false,
   currentIssueNotFound: false,
   currentIssueError: null,
   currentIssueNo: null,
   currentIssueTimeline: [],
   currentIssueTimelineLoading: false,
   currentIssueTimelineError: null,
   currentIssueTimelineNo: null,
   currentIssueNotes: null,
   currentIssueNotesLoading: false,
   currentIssueNotesError: null,
   currentIssueNotesChannelId: null,
   currentIssueNoteDraft: '',
   currentIssueOptimisticMessages: [],
   currentIssueNoteSubmittingId: null,
   currentIssueNoteError: null,
   currentIssueNoteLastCompletedId: null,
   currentIssueNoteDeleteSaving: false,
   currentIssueNoteDeleteError: null,
   currentIssueNoteDeleteCompletedId: null,
   currentIssueAssignmentSaving: false,
   currentIssueAssignmentError: null,
   currentIssueAssignmentRefreshPending: false,
   currentIssueAssignmentCompletedAt: null,
   currentIssueTagsSaving: false,
   currentIssueTagsCreating: false,
   currentIssueTagsError: null,
   currentIssueTagsCreateError: null,
   currentIssueTagsRefreshPending: false,
   currentIssueTagsCreateCompletedAt: null,
   currentIssueLifecycleSaving: false,
   currentIssueLifecycleError: null,
   currentIssueLifecycleRefreshPending: false,
   currentIssueDefinitionSaving: false,
   currentIssueDefinitionError: null,
   currentIssueDefinitionRefreshPending: false,
   currentIssueDeadlineSaving: false,
   currentIssueDeadlineError: null,
   currentIssueDeadlineRefreshPending: false,
   filters: {
      lifecycleView: 'OPEN',
      operationalView: null,
      idIssueSortType: null,
      searchText: '',
      nosLocation: [],
      noPriority: null
   }
};

const dedupeIssues = (issues) => {
   const seen = new Set();
   return _.filter(issues, (issue) => {
      const key = issue && (issue.id || issue.noIssue);
      if (key == null || seen.has(String(key))) return false;
      seen.add(String(key));
      return true;
   });
};

class IssueStore extends Refluxr.Store {
   constructor() {
      super();
      this.listenables = [IssueActions, AccountActions];
      this.state = _.cloneDeep(INITIAL_STATE);
   }

   logoutUserSync() {
      this.setState(_.cloneDeep(INITIAL_STATE));
   }

   onResetIssuesSync() {
      this.setState(_.cloneDeep(INITIAL_STATE));
   }

   onResetIssueDetailSync() {
      this.setState({
         currentIssue: null,
         currentIssueProvisional: false,
         currentIssueLoading: false,
         currentIssueNotFound: false,
         currentIssueError: null,
         currentIssueNo: null,
         currentIssueTimeline: [],
         currentIssueTimelineLoading: false,
         currentIssueTimelineError: null,
         currentIssueTimelineNo: null,
         currentIssueNotes: null,
         currentIssueNotesLoading: false,
         currentIssueNotesError: null,
         currentIssueNotesChannelId: null,
         currentIssueNoteDraft: '',
         currentIssueOptimisticMessages: [],
         currentIssueNoteSubmittingId: null,
         currentIssueNoteError: null,
         currentIssueNoteLastCompletedId: null,
         currentIssueNoteDeleteSaving: false,
         currentIssueNoteDeleteError: null,
         currentIssueNoteDeleteCompletedId: null,
         currentIssueAssignmentSaving: false,
         currentIssueAssignmentError: null,
         currentIssueAssignmentRefreshPending: false,
         currentIssueAssignmentCompletedAt: null,
         currentIssueTagsSaving: false,
         currentIssueTagsCreating: false,
         currentIssueTagsError: null,
         currentIssueTagsCreateError: null,
         currentIssueTagsRefreshPending: false,
         currentIssueTagsCreateCompletedAt: null,
         currentIssueLifecycleSaving: false,
         currentIssueLifecycleError: null,
         currentIssueLifecycleRefreshPending: false,
         currentIssueDefinitionSaving: false,
         currentIssueDefinitionError: null,
         currentIssueDefinitionRefreshPending: false,
         currentIssueDeadlineSaving: false,
         currentIssueDeadlineError: null,
         currentIssueDeadlineRefreshPending: false
      });
   }

   onResetRaiseIssueSync() {
      this.setState({
         raiseIssueSaving: false,
         raiseIssueError: null,
         raiseIssueCompletedAt: null,
         raisedIssue: null
      });
   }

   onBeginRaiseIssueSync() {
      this.setState({
         raiseIssueSaving: true,
         raiseIssueError: null,
         raisedIssue: null
      });
   }

   onRaiseIssueCompleted(response) {
      const content = response && response.data ? response.data.content || {} : {};
      const issue = content.issue || content.instance || content;
      this.setState({
         raiseIssueSaving: false,
         raiseIssueError: null,
         raiseIssueCompletedAt: Date.now(),
         raisedIssue: issue,
         issuesRefreshPending: true
      });
   }

   onRaiseIssueFailed(response) {
      const data = response && response.response && response.response.data;
      const message = (data && (data.message || data.error)) || 'The Issue could not be raised. Please try again.';
      this.setState({
         raiseIssueSaving: false,
         raiseIssueError: message,
         raisedIssue: null
      });
   }

   onBeginGetIssueSync({ noIssue }) {
      const provisionalIssue = _.find(this.state.issues || [], (issue) => String(issue.noIssue) === String(noIssue)) || null;
      const lifecycleRefreshPending = Boolean(this.state.currentIssueLifecycleRefreshPending);
      const definitionRefreshPending = Boolean(this.state.currentIssueDefinitionRefreshPending);
      const preserveCurrentIssue = lifecycleRefreshPending || definitionRefreshPending;
      this.setState({
         currentIssue: preserveCurrentIssue ? this.state.currentIssue : provisionalIssue,
         currentIssueProvisional: preserveCurrentIssue ? false : Boolean(provisionalIssue),
         currentIssueLoading: true,
         currentIssueNotFound: false,
         currentIssueError: null,
         currentIssueNo: Number(noIssue),
         currentIssueNotes: null,
         currentIssueNotesLoading: false,
         currentIssueNotesError: null,
         currentIssueNotesChannelId: null,
         currentIssueNoteDraft: '',
         currentIssueOptimisticMessages: [],
         currentIssueNoteSubmittingId: null,
         currentIssueNoteError: null,
         currentIssueNoteLastCompletedId: null,
         currentIssueNoteDeleteSaving: false,
         currentIssueNoteDeleteError: null,
         currentIssueNoteDeleteCompletedId: null,
         currentIssueAssignmentSaving: false,
         currentIssueAssignmentError: null,
         currentIssueAssignmentRefreshPending: false,
         currentIssueAssignmentCompletedAt: null,
         currentIssueTagsSaving: false,
         currentIssueTagsCreating: false,
         currentIssueTagsError: null,
         currentIssueTagsCreateError: null,
         currentIssueTagsRefreshPending: false,
         currentIssueTagsCreateCompletedAt: null,
         currentIssueLifecycleSaving: lifecycleRefreshPending ? this.state.currentIssueLifecycleSaving : false,
         currentIssueLifecycleError: lifecycleRefreshPending ? this.state.currentIssueLifecycleError : null,
         currentIssueLifecycleRefreshPending: lifecycleRefreshPending,
         currentIssueDefinitionSaving: definitionRefreshPending ? this.state.currentIssueDefinitionSaving : false,
         currentIssueDefinitionError: definitionRefreshPending ? this.state.currentIssueDefinitionError : null,
         currentIssueDefinitionRefreshPending: definitionRefreshPending
      });
   }


   onBeginGetIssueTimelineSync({ noIssue }) {
      this.setState({
         currentIssueTimeline: [],
         currentIssueTimelineLoading: true,
         currentIssueTimelineError: null,
         currentIssueTimelineNo: Number(noIssue)
      });
   }

   onBeginGetIssueNotesSync({ idChannel }) {
      const sameChannel = String(idChannel || '') === String(this.state.currentIssueNotesChannelId || '');
      this.setState({
         currentIssueNotes: sameChannel ? this.state.currentIssueNotes : null,
         currentIssueNotesLoading: true,
         currentIssueNotesError: null,
         currentIssueNotesChannelId: idChannel || null
      });
   }

   onSetIssueNoteDraftSync({ draft = '' }) {
      this.setState({ currentIssueNoteDraft: draft, currentIssueNoteError: null });
   }

   onBeginAddIssueCommentSync({ message }) {
      const messages = upsertOptimisticMessage(this.state.currentIssueOptimisticMessages || [], message);

      this.setState({
         currentIssueOptimisticMessages: messages,
         currentIssueNoteSubmittingId: message._id,
         currentIssueNoteError: null,
         currentIssueNoteDraft: ''
      });
   }

   onRemoveIssueOptimisticMessageSync({ id }) {
      this.setState({
         currentIssueOptimisticMessages: removeOptimisticMessage(this.state.currentIssueOptimisticMessages || [], id),
         currentIssueNoteError: null
      });
   }

   onBeginDeleteIssueCommentSync() {
      this.setState({
         currentIssueNoteDeleteSaving: true,
         currentIssueNoteDeleteError: null,
         currentIssueNoteDeleteCompletedId: null
      });
   }

   onResetDeleteIssueCommentSync() {
      this.setState({
         currentIssueNoteDeleteSaving: false,
         currentIssueNoteDeleteError: null,
         currentIssueNoteDeleteCompletedId: null
      });
   }

   onBeginAssignIssueSync() {
      this.setState({
         currentIssueAssignmentSaving: true,
         currentIssueAssignmentError: null,
         currentIssueAssignmentRefreshPending: false
      });
   }

   onBeginManageIssueTagsSync() {
      this.setState({
         currentIssueTagsSaving: true,
         currentIssueTagsError: null,
         currentIssueTagsRefreshPending: false
      });
   }

   onBeginCreateIssueTagSync() {
      this.setState({
         currentIssueTagsCreating: true,
         currentIssueTagsCreateError: null
      });
   }

   onBeginCloseIssueSync() {
      this.setState({
         currentIssueLifecycleSaving: true,
         currentIssueLifecycleError: null,
         currentIssueLifecycleRefreshPending: false
      });
   }

   onBeginReopenIssueSync() {
      this.onBeginCloseIssueSync();
   }

   onBeginEditIssueDefinitionSync() {
      this.setState({
         currentIssueDefinitionSaving: true,
         currentIssueDefinitionError: null,
         currentIssueDefinitionRefreshPending: false
      });
   }

   onBeginResetIssueDeadlineSync() {
      this.setState({
         currentIssueDeadlineSaving: true,
         currentIssueDeadlineError: null,
         currentIssueDeadlineRefreshPending: false
      });
   }

   onResetIssueDefinitionSync() {
      this.setState({
         currentIssueDefinitionSaving: false,
         currentIssueDefinitionError: null,
         currentIssueDefinitionRefreshPending: false
      });
   }

   onResetIssueDeadlineSync() {
      this.setState({
         currentIssueDeadlineSaving: false,
         currentIssueDeadlineError: null,
         currentIssueDeadlineRefreshPending: false
      });
   }

   onResetIssueLifecycleSync() {
      this.setState({
         currentIssueLifecycleSaving: false,
         currentIssueLifecycleError: null,
         currentIssueLifecycleRefreshPending: false
      });
   }

   onSetIssueFiltersSync(filters) {
      this.setState({
         filters: { ...this.state.filters, ...filters },
         error: null
      });
   }

   onBeginFetchIssuesSync({ page = 1 }) {
      const isInitialLoad = page === 1 && !this.state.initialised;
      this.setState({
         requestPage: page,
         loadingInitial: isInitialLoad,
         refreshing: page === 1 && !isInitialLoad,
         loadingMore: page > 1,
         error: null
      });
   }

   onGetIssueCompleted(response) {
      const content = response && response.data ? response.data.content : null;
      const issue = content && content.issue ? content.issue : null;
      const assignmentRefreshCompleted = Boolean(this.state.currentIssueAssignmentRefreshPending);
      const lifecycleRefreshCompleted = Boolean(this.state.currentIssueLifecycleRefreshPending);
      const definitionRefreshCompleted = Boolean(this.state.currentIssueDefinitionRefreshPending);
      const deadlineRefreshCompleted = Boolean(this.state.currentIssueDeadlineRefreshPending);
      this.setState({
         currentIssue: issue,
         currentIssueProvisional: false,
         currentIssueLoading: false,
         currentIssueNotFound: !issue,
         currentIssueError: null,
         currentIssueAssignmentSaving: assignmentRefreshCompleted ? false : this.state.currentIssueAssignmentSaving,
         currentIssueAssignmentRefreshPending: assignmentRefreshCompleted ? false : this.state.currentIssueAssignmentRefreshPending,
         currentIssueAssignmentCompletedAt: assignmentRefreshCompleted ? Date.now() : this.state.currentIssueAssignmentCompletedAt,
         currentIssueLifecycleSaving: lifecycleRefreshCompleted ? false : this.state.currentIssueLifecycleSaving,
         currentIssueLifecycleError: lifecycleRefreshCompleted ? null : this.state.currentIssueLifecycleError,
         currentIssueLifecycleRefreshPending: lifecycleRefreshCompleted ? false : this.state.currentIssueLifecycleRefreshPending,
         currentIssueDefinitionSaving: definitionRefreshCompleted ? false : this.state.currentIssueDefinitionSaving,
         currentIssueDefinitionError: definitionRefreshCompleted ? null : this.state.currentIssueDefinitionError,
         currentIssueDefinitionRefreshPending: definitionRefreshCompleted ? false : this.state.currentIssueDefinitionRefreshPending,
         currentIssueDeadlineSaving: deadlineRefreshCompleted ? false : this.state.currentIssueDeadlineSaving,
         currentIssueDeadlineError: deadlineRefreshCompleted ? null : this.state.currentIssueDeadlineError,
         currentIssueDeadlineRefreshPending: deadlineRefreshCompleted ? false : this.state.currentIssueDeadlineRefreshPending
      });
   }

   onAssignIssueCompleted() {
      this.setState({
         currentIssueAssignmentError: null,
         currentIssueAssignmentRefreshPending: true,
         issuesRefreshPending: true
      });
   }

   onAssignIssueFailed(response) {
      const data = response && response.response && response.response.data;
      const message = (data && (data.message || data.error)) || 'Issue assignment could not be saved. Please try again.';
      this.setState({
         currentIssueAssignmentSaving: false,
         currentIssueAssignmentError: message,
         currentIssueAssignmentRefreshPending: false
      });
   }

   onManageIssueTagsCompleted(response) {
      const content = response && response.data ? response.data.content || {} : {};
      this.setState({
         currentIssue: content.issue || this.state.currentIssue,
         currentIssueTagsSaving: false,
         currentIssueTagsError: null,
         currentIssueTagsRefreshPending: true,
         issuesRefreshPending: true
      });
   }

   onManageIssueTagsFailed(response) {
      const data = response && response.response && response.response.data;
      const message = (data && (data.message || data.error)) || 'Issue tags could not be saved. Please try again.';
      this.setState({
         currentIssueTagsSaving: false,
         currentIssueTagsError: message,
         currentIssueTagsRefreshPending: false
      });
   }

   onCreateIssueTagCompleted(response) {
      const content = response && response.data ? response.data.content || {} : {};
      const issue = content.issues && content.issues.length ? content.issues[0] : null;
      this.setState({
         currentIssue: issue || this.state.currentIssue,
         currentIssueTagsCreating: false,
         currentIssueTagsCreateError: null,
         currentIssueTagsCreateCompletedAt: Date.now(),
         issuesRefreshPending: true
      });
   }

   onCreateIssueTagFailed(response) {
      const data = response && response.response && response.response.data;
      const message = (data && (data.message || data.error)) || 'The Issue tag could not be created. Please try again.';
      this.setState({
         currentIssueTagsCreating: false,
         currentIssueTagsCreateError: message
      });
   }

   onUpdateIssueDefinitionCompleted(response) {
      const content = response && response.data ? response.data.content || {} : {};
      this.setState({
         currentIssue: content.issue || this.state.currentIssue,
         currentIssueDefinitionSaving: false,
         currentIssueDefinitionError: null,
         currentIssueDefinitionRefreshPending: true,
         issuesRefreshPending: true
      });
   }

   onUpdateIssueDefinitionFailed(response) {
      const data = response && response.response && response.response.data;
      const message = (data && (data.message || data.error)) || 'The Issue could not be updated. Please try again.';
      this.setState({
         currentIssueDefinitionSaving: false,
         currentIssueDefinitionError: message,
         currentIssueDefinitionRefreshPending: false
      });
   }

   onResetIssueDeadlineCompleted(response) {
      const content = response && response.data ? response.data.content || {} : {};
      this.setState({
         currentIssue: content.issue || this.state.currentIssue,
         currentIssueDeadlineSaving: false,
         currentIssueDeadlineError: null,
         currentIssueDeadlineRefreshPending: true,
         issuesRefreshPending: true
      });
   }

   onResetIssueDeadlineFailed(response) {
      const data = response && response.response && response.response.data;
      const message = (data && (data.message || data.error)) || 'The Issue deadline could not be reset. Please try again.';
      this.setState({
         currentIssueDeadlineSaving: false,
         currentIssueDeadlineError: message,
         currentIssueDeadlineRefreshPending: false
      });
   }

   onCloseIssueCompleted(response) {
      const content = response && response.data ? response.data.content || {} : {};
      this.setState({
         currentIssue: content.issue || this.state.currentIssue,
         currentIssueLifecycleSaving: false,
         currentIssueLifecycleError: null,
         currentIssueLifecycleRefreshPending: true,
         issuesRefreshPending: true
      });
   }

   onCloseIssueFailed(response) {
      const data = response && response.response && response.response.data;
      const message = (data && (data.message || data.error)) || 'The Issue could not be closed. Please try again.';
      this.setState({
         currentIssueLifecycleSaving: false,
         currentIssueLifecycleError: message,
         currentIssueLifecycleRefreshPending: false
      });
   }

   onReopenIssueCompleted(response) {
      this.onCloseIssueCompleted(response);
   }

   onReopenIssueFailed(response) {
      const data = response && response.response && response.response.data;
      const message = (data && (data.message || data.error)) || 'The Issue could not be reopened. Please try again.';
      this.setState({
         currentIssueLifecycleSaving: false,
         currentIssueLifecycleError: message,
         currentIssueLifecycleRefreshPending: false
      });
   }

   onGetIssueFailed(response) {
      const status = response && response.status;
      const responseStatus = response && response.response && response.response.status;
      const notFound = status === 404 || responseStatus === 404;

      if (this.state.currentIssueAssignmentRefreshPending) {
         this.setState({
            currentIssueProvisional: false,
            currentIssueLoading: false,
            currentIssueNotFound: false,
            currentIssueError: null,
            currentIssueAssignmentSaving: false,
            currentIssueAssignmentError: 'Assignment was saved, but the Issue could not be refreshed. Please try again.',
            currentIssueAssignmentRefreshPending: false
         });
         return;
      }

      if (this.state.currentIssueLifecycleRefreshPending) {
         this.setState({
            currentIssueProvisional: false,
            currentIssueLoading: false,
            currentIssueNotFound: false,
            currentIssueError: null,
            currentIssueLifecycleSaving: false,
            currentIssueLifecycleError: 'The lifecycle change was saved, but the Issue could not be refreshed. Please try again.',
            currentIssueLifecycleRefreshPending: false
         });
         return;
      }

      if (this.state.currentIssueDefinitionRefreshPending) {
         this.setState({
            currentIssueProvisional: false,
            currentIssueLoading: false,
            currentIssueNotFound: false,
            currentIssueError: null,
            currentIssueDefinitionSaving: false,
            currentIssueDefinitionError: 'The Issue was updated, but the latest details could not be refreshed. Please try again.',
            currentIssueDefinitionRefreshPending: false
         });
         return;
      }

      if (this.state.currentIssueDeadlineRefreshPending) {
         this.setState({
            currentIssueProvisional: false,
            currentIssueLoading: false,
            currentIssueNotFound: false,
            currentIssueError: null,
            currentIssueDeadlineSaving: false,
            currentIssueDeadlineError: 'The deadline was reset, but the Issue could not be refreshed. Please try again.',
            currentIssueDeadlineRefreshPending: false
         });
         return;
      }

      this.setState({
         currentIssue: null,
         currentIssueProvisional: false,
         currentIssueLoading: false,
         currentIssueNotFound: notFound,
         currentIssueError: notFound ? null : 'Unable to load issue.'
      });
   }


   onGetIssueTimelineCompleted(response) {
      const requestedNoIssue = Number(response && response.input && response.input.noIssue);
      if (requestedNoIssue !== Number(this.state.currentIssueTimelineNo)) return;

      const content = response && response.data ? response.data.content || {} : {};
      this.setState({
         currentIssueTimeline: content.timeline || [],
         currentIssueTimelineLoading: false,
         currentIssueTimelineError: null
      });
   }

   onGetIssueTimelineFailed(response) {
      const requestedNoIssue = Number(response && response.input && response.input.noIssue);
      if (requestedNoIssue !== Number(this.state.currentIssueTimelineNo)) return;

      this.setState({
         currentIssueTimeline: [],
         currentIssueTimelineLoading: false,
         currentIssueTimelineError: 'Timeline could not be loaded.'
      });
   }

   onGetIssueNotesCompleted(response) {
      const requestedIdChannel = response && response.input && response.input.idChannel;
      if (String(requestedIdChannel) !== String(this.state.currentIssueNotesChannelId)) return;

      const content = response && response.data ? response.data.content || {} : {};
      const channel = content.channel || null;
      const optimistic = reconcileOptimisticMessages(
         this.state.currentIssueOptimisticMessages || [],
         (channel && channel.frames) || []
      );

      this.setState({
         currentIssueNotes: channel,
         currentIssueOptimisticMessages: optimistic,
         currentIssueNotesLoading: false,
         currentIssueNotesError: null,
         currentIssueNoteLastCompletedId: null
      });
   }

   onAddIssueCommentCompleted(response) {
      const idFrame = response && response.input && response.input.idFrame;
      const content = response && response.data ? response.data.content : null;
      const issue = content && content.issue ? content.issue : content;
      this.setState({
         currentIssue: issue && issue.id ? issue : this.state.currentIssue,
         currentIssueOptimisticMessages: markOptimisticMessage(
            this.state.currentIssueOptimisticMessages || [],
            idFrame,
            { pending: false, failed: false }
         ),
         currentIssueNoteSubmittingId: null,
         currentIssueNoteError: null,
         currentIssueNoteLastCompletedId: idFrame || null
      });
   }

   onAddIssueCommentFailed(response) {
      const idFrame = response && response.input && response.input.idFrame;
      this.setState({
         currentIssueOptimisticMessages: markOptimisticMessage(
            this.state.currentIssueOptimisticMessages || [],
            idFrame,
            { pending: false, failed: true }
         ),
         currentIssueNoteSubmittingId: null,
         currentIssueNoteError: 'Your note could not be added. Please try again.'
      });
   }

   onDeleteIssueCommentCompleted(response) {
      const idFrame = response && response.input && response.input.idFrame;
      const content = response && response.data ? response.data.content : null;
      const issue = content && content.issue ? content.issue : content;
      this.setState({
         currentIssue: issue && issue.id ? issue : this.state.currentIssue,
         currentIssueNoteDeleteSaving: false,
         currentIssueNoteDeleteError: null,
         currentIssueNoteDeleteCompletedId: idFrame || null
      });
   }

   onDeleteIssueCommentFailed(response) {
      const data = response && response.response && response.response.data;
      const message = (data && (data.message || data.error)) || 'The note could not be deleted. Please try again.';
      this.setState({
         currentIssueNoteDeleteSaving: false,
         currentIssueNoteDeleteError: message,
         currentIssueNoteDeleteCompletedId: null
      });
   }

   onGetIssueNotesFailed(response) {
      const requestedIdChannel = response && response.input && response.input.idChannel;
      if (String(requestedIdChannel) !== String(this.state.currentIssueNotesChannelId)) return;

      this.setState({
         currentIssueNotes: this.state.currentIssueNotes,
         currentIssueNotesLoading: false,
         currentIssueNotesError: 'Notes could not be loaded.'
      });
   }

   onFetchIssuesCompleted(response) {
      const data = response && response.data ? response.data : {};
      const content = data.content || [];
      const paging = data.paging || { count: content.length, total: content.length, limit: 20, page: this.state.requestPage };
      const nextIssues = this.state.requestPage > 1 ? dedupeIssues([...(this.state.issues || []), ...content]) : dedupeIssues(content);

      this.setState({
         issues: nextIssues,
         paging,
         summary: data.summary || this.state.summary || EMPTY_SUMMARY,
         initialised: true,
         loadingInitial: false,
         refreshing: false,
         loadingMore: false,
         error: null,
         issuesRefreshPending: false
      });
   }

   onFetchIssuesFailed(response) {
      const message = response && response.data && response.data.message ? response.data.message : 'Unable to load issues.';
      this.setState({ loadingInitial: false, refreshing: false, loadingMore: false, error: message });
   }
}

export default IssueStore;
